import { useCallback, useMemo, useState } from "react";

import { LocalStorageKey } from "@/storage/local-storage";
import { create } from "@bufbuild/protobuf";

import FlexWrapper, { FlexDirection } from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import { SourceStatus, SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  ListSourcesRequestSchema,
  SourceFilter,
  SourceFilterSchema,
} from "@gx/ts-contracts/services/source/api/source_pb";
import { SortBy, SortingRequestSchema, SortOrder } from "@gx/ts-contracts/shared/sorting_pb";

import { ALL_SOURCES } from "@/pages/sources/components/create/constants";
import {
  DEFAULT_SOURCES_VIEW,
  FILTERABLE_SOURCE_STATUSES,
  SourcesView,
} from "@/pages/sources/constants";
import SourcesPageBody from "@/pages/sources/SourcesPageBody";
import SourcesPageHeader from "@/pages/sources/SourcesPageHeader";

import { useListSourcesInfiniteQuery } from "@/api/queries/sources";

import useLocalStorage from "@/hooks/useLocalStorage";

interface SourcesPageState {
  search: string;
  sortBy: SortBy;
  sortOrder: SortOrder;
  sourceTypes: SourceType[];
  sourceStatuses: SourceStatus[];
  includeDeleted: SourceFilter["includeDeleted"];
}

const FILTERABLE_SOURCE_TYPES = ALL_SOURCES.filter((s) => s !== SourceType.UNSPECIFIED);

const DEFAULT_STATE: SourcesPageState = {
  search: "",
  sortBy: SortBy.UPDATED_AT,
  sortOrder: SortOrder.DESC,
  sourceTypes: FILTERABLE_SOURCE_TYPES,
  sourceStatuses: FILTERABLE_SOURCE_STATUSES,
  includeDeleted: false,
};

interface SourcesPageProps {
  onClose?: () => void;
}

const SourcesPage = ({ onClose }: SourcesPageProps) => {
  const [state, setState] = useState<SourcesPageState>(DEFAULT_STATE);
  const [sourceView, setSourceView] = useLocalStorage(
    LocalStorageKey.SOURCES_VIEW,
    DEFAULT_SOURCES_VIEW,
    SourcesView,
  );

  const listSourcesInput = useMemo(() => {
    const allTypesSelected = state.sourceTypes.length === FILTERABLE_SOURCE_TYPES.length;
    const allStatusesSelected = state.sourceStatuses.length === FILTERABLE_SOURCE_STATUSES.length;
    return create(ListSourcesRequestSchema, {
      sorting: create(SortingRequestSchema, {
        sortBy: state.sortBy,
        sortOrder: state.sortOrder,
      }),
      filter: create(SourceFilterSchema, {
        name: state.search || undefined,
        sourceTypes: allTypesSelected ? [] : state.sourceTypes,
        sourceStatuses: allStatusesSelected ? [] : state.sourceStatuses,
        includeDeleted: state.includeDeleted,
      }),
    });
  }, [
    state.sortBy,
    state.sortOrder,
    state.search,
    state.sourceTypes,
    state.sourceStatuses,
    state.includeDeleted,
  ]);

  const {
    data,
    isLoading: isLoadingSources,
    isError: isErrorSources,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useListSourcesInfiniteQuery({
    input: listSourcesInput,
  });

  const sources = useMemo(() => data?.pages.flatMap((p) => p.sources) ?? [], [data]);

  const handleSearchChange = useCallback((search: string) => {
    setState((prev) => ({ ...prev, search }));
  }, []);

  const handleSortByChange = useCallback((sortBy: SortBy) => {
    setState((prev) => ({ ...prev, sortBy }));
  }, []);

  const handleSortOrderChange = useCallback((sortOrder: SortOrder) => {
    setState((prev) => ({ ...prev, sortOrder }));
  }, []);

  const handleSourceTypesChange = useCallback((sourceTypes: SourceType[]) => {
    setState((prev) => ({ ...prev, sourceTypes }));
  }, []);

  const handleSourceStatusesChange = useCallback((sourceStatuses: SourceStatus[]) => {
    setState((prev) => ({ ...prev, sourceStatuses }));
  }, []);

  const handleIncludeDeletedChange = useCallback((includeDeleted: boolean) => {
    setState((prev) => ({ ...prev, includeDeleted }));
  }, []);

  return (
    <FlexWrapper direction={FlexDirection.COLUMN} fillHeight fillWidth>
      <SourcesPageHeader
        isLoading={isLoadingSources}
        search={state.search}
        onSearchChange={handleSearchChange}
        sortBy={state.sortBy}
        sortOrder={state.sortOrder}
        onSortByChange={handleSortByChange}
        onSortOrderChange={handleSortOrderChange}
        sourceTypes={state.sourceTypes}
        onSourceTypesChange={handleSourceTypesChange}
        sourceStatuses={state.sourceStatuses}
        onSourceStatusesChange={handleSourceStatusesChange}
        sourceView={sourceView}
        onSourceViewChange={setSourceView}
        includeDeleted={state.includeDeleted ?? false}
        onIncludeDeletedChange={handleIncludeDeletedChange}
        onClose={onClose}
      />
      <HorizontalDivider />
      <SourcesPageBody
        sources={sources}
        isLoading={isLoadingSources}
        isError={isErrorSources}
        search={state.search}
        sourceTypes={state.sourceTypes}
        sourceStatuses={state.sourceStatuses}
        sourceView={sourceView}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={() => void fetchNextPage()}
      />
    </FlexWrapper>
  );
};

export default SourcesPage;
